import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, MessageCircle, Mail, CheckCircle, ExternalLink, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/utils/api';
import axios from 'axios';

const SatisfactionPage = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [reminderPref, setReminderPref] = useState('whatsapp');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/satisfaction/${surveyId}`);
        setSurvey(res.data);
        if (res.data.status === 'completed') setSubmitted(true);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [surveyId]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/satisfaction/${surveyId}/submit`, {
        rating,
        comment,
        reminder_preference: reminderPref,
      });
      setSubmitted(true);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <p className="text-slate-400">Chargement...</p>
    </div>
  );

  if (!survey) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="p-8 rounded-2xl text-center max-w-md">
        <p className="text-slate-600">Lien invalide ou enquete introuvable.</p>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="p-8 rounded-2xl text-center max-w-md shadow-lg" data-testid="survey-thank-you">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Merci pour votre retour !</h1>
        <p className="text-slate-600 mb-6">Votre avis est precieux et nous aide a nous ameliorer.</p>
        {survey.google_reviews_url && (
          <a
            href={survey.google_reviews_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors"
            data-testid="google-review-link-after"
          >
            <ExternalLink className="w-4 h-4" /> Laissez-nous un avis Google
          </a>
        )}
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4" data-testid="satisfaction-page">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Votre avis compte</h1>
          <p className="text-slate-600 mt-2">Suite a votre visite dans nos ateliers</p>
        </div>

        {/* Vehicle info */}
        {survey.vehicle_info && (
          <Card className="p-3 rounded-xl bg-slate-100 border-0 flex items-center gap-2 justify-center">
            <Car className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">{survey.vehicle_info}</span>
          </Card>
        )}

        {/* Google Reviews CTA */}
        {survey.google_reviews_url && (
          <Card className="p-6 rounded-2xl bg-blue-50 border border-blue-100 text-center shadow-sm" data-testid="google-review-card">
            <p className="text-sm text-blue-700 mb-3 font-medium">Partagez votre experience sur Google</p>
            <a
              href={survey.google_reviews_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors text-sm"
              data-testid="google-review-link"
            >
              <ExternalLink className="w-4 h-4" /> Laisser un avis Google
            </a>
          </Card>
        )}

        {/* Rating */}
        <Card className="p-6 rounded-2xl shadow-sm" data-testid="rating-card">
          <h2 className="text-base font-bold text-slate-900 mb-4 text-center">Comment avez-vous trouve notre service ?</h2>
          <div className="flex justify-center gap-2 mb-4" data-testid="star-rating">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(n)}
                className="transition-transform hover:scale-110"
                data-testid={`star-${n}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    n <= (hoverRating || rating)
                      ? 'fill-amber-400 text-amber-400'
                      : 'text-slate-200'
                  } transition-colors`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-slate-500">
              {rating === 5 ? 'Excellent !' : rating === 4 ? 'Tres bien !' : rating === 3 ? 'Correct' : rating === 2 ? 'A ameliorer' : 'Insatisfait'}
            </p>
          )}
        </Card>

        {/* Comment */}
        <Card className="p-6 rounded-2xl shadow-sm">
          <h2 className="text-base font-bold text-slate-900 mb-3">Un commentaire ? (optionnel)</h2>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Dites-nous ce que vous avez apprecie ou ce que nous pourrions ameliorer..."
            className="resize-none"
            data-testid="comment-input"
          />
        </Card>

        {/* Reminder Preference */}
        {survey.next_service_date && (
          <Card className="p-6 rounded-2xl shadow-sm" data-testid="reminder-card">
            <h2 className="text-base font-bold text-slate-900 mb-2">Rappel prochain service</h2>
            <p className="text-sm text-slate-500 mb-4">
              Votre prochain service est prevu le <strong>{survey.next_service_date}</strong>. Souhaitez-vous etre rappele 1 mois avant ?
            </p>
            <div className="space-y-2">
              {[
                { value: 'whatsapp', label: 'Par WhatsApp', icon: MessageCircle, color: 'text-emerald-600' },
                { value: 'email', label: 'Par Email', icon: Mail, color: 'text-blue-600' },
                { value: 'both', label: 'WhatsApp + Email', icon: CheckCircle, color: 'text-violet-600' },
                { value: 'none', label: 'Non merci', icon: null, color: 'text-slate-400' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setReminderPref(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    reminderPref === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`reminder-${opt.value}`}
                >
                  {opt.icon && <opt.icon className={`w-5 h-5 ${opt.color}`} />}
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  {reminderPref === opt.value && <CheckCircle className="w-4 h-4 text-blue-500 ml-auto" />}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="w-full py-6 text-base rounded-xl bg-slate-900 hover:bg-slate-800"
          data-testid="submit-feedback-btn"
        >
          {submitting ? 'Envoi en cours...' : 'Envoyer mon avis'}
        </Button>
      </div>
    </div>
  );
};

export default SatisfactionPage;
